import apiClient from "config/apiClient";
import Notify from "config/notify";
import {
  ItemPriceSelect,
  ItemPriceSelectResponse,
  ItemPriceUpdateRequest,
  ItemSelectRequest,
  ItemSelectResponse,
  ItemUpdateRequest,
} from "../types/item";

/**
 * Handles errors by logging them or sending them to an error tracking service.
 * @param {string} message - The error message.
 * @param {any} error - The error object.
 */
const handleError = (message: string, error: any) => {
  // Replace this with your error handling logic
  console.error(message, error);
};

/**
 * Fetches the list of items from the API.
 * @param {ItemSelectRequest} params - The parameters for selecting items.
 * @returns {Promise<ItemSelectResponse | undefined>} The data from the API response.
 * @throws Will throw an error if the request fails.
 */
export const fetchItems = async (
  params: ItemSelectRequest,
): Promise<ItemSelectResponse | undefined> => {
  try {
    const response = await apiClient.post<ItemSelectResponse>("/mst/item/select", params);
    return response.data;
  } catch (error) {
    handleError("Error fetching items:", error);
    throw error;
  }
};

/**
 * Updates an item with the provided data.
 * @param {ItemUpdateRequest} data - The data to update the item with.
 * @returns {Promise<void>} A promise that resolves when the item is updated.
 * @throws Will throw an error if the update operation fails.
 */
export const updateItem = async (data: ItemUpdateRequest): Promise<void> => {
  try {
    const response = await apiClient.post("/mst/item/update", data);
    if (response.status === 200) {
      Notify.setNotification({
        show: true,
        title: "更新が完了しました",
        confirmLabel: "OK",
        actionsPosition: "center",
      });
    } else {
      throw new Error(response.message);
    }
  } catch (error) {
    handleError("Error updating item:", error);
    throw error;
  }
};

/**
 * Fetches the price of an item based on the provided parameters.
 *
 * @param {ItemPriceSelect} params - The parameters used to select the item price.
 * @returns {Promise<ItemPriceSelectResponse>} A promise that resolves to the item price response.
 * @throws Will throw an error if the request fails.
 */
export const fetchItemPrice = async (params: ItemPriceSelect): Promise<ItemPriceSelectResponse> => {
  try {
    const response = await apiClient.post<ItemPriceSelectResponse>(
      "/mst/item/price/select",
      params,
    );
    return response.data || { list: [], total: 0 };
  } catch (error) {
    handleError("Error fetching item price:", error);
    throw error;
  }
};

/**
 * Updates the price of an item.
 * @param {ItemPriceUpdateRequest} data - The request data containing the item price update information.
 * @returns {Promise<void>} A promise that resolves when the item price update is complete.
 */
export const updateItemPrice = async (data: ItemPriceUpdateRequest): Promise<void> => {
  await apiClient.post("/mst/item/price/update", data);
};
